/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import { AttributeSection } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';
import { AbsentRawMaterials } from './absent-raw-materials';
import { Certificates } from './certificates';
import { EnergySources } from './energy-sources';
import { getProductTypeAttributesFromIPR, getSpecialAttributesFromIPR, getSpecialEndUsesFromIPR } from './helpers';
import { ProductTypeAttributes } from './product-type';
import { RecyclabilityAttributes } from './recyclability';
import { SpecialAttributes } from './special-attributes';
import { SpecialEndUses } from './special-end-uses';
import { TargetIndustries } from './target-industries';

interface AttributesSectionProps {
  mergedIpr: Partial<IPRAttributes>;
  updateProductIPR: (updates: Partial<IPRAttributes>) => void;
  /** Each section's warning line (`getAttributeWarnings`), for the attributes the results will use. */
  warnings: Partial<Record<AttributeSection, string>>;
}

export function AttributesSection({ mergedIpr, updateProductIPR, warnings }: AttributesSectionProps) {
  return (
    <>
      <RecyclabilityAttributes
        recyclability={mergedIpr.Recyclability?.map(r => ({ id: r, title: r })) || []}
        onRecyclabilityChange={recyclability => {
          updateProductIPR({ Recyclability: recyclability.map(r => r.id) });
        }}
        warning={warnings.recyclability}
      />
      <EnergySources
        energySources={mergedIpr.EnergySourcesUsed?.map(e => ({ id: e, title: e })) || []}
        onEnergySourcesChange={energySources => {
          updateProductIPR({ EnergySourcesUsed: energySources.map(e => e.id) });
        }}
        warning={warnings.energySources}
      />
      <Certificates
        certificates={mergedIpr.Certifications || []}
        onCertificatesChange={certificates => {
          updateProductIPR({ Certifications: certificates });
        }}
        warning={warnings.certificates}
      />
      <TargetIndustries
        industries={mergedIpr.TargetIndustry?.map(t => ({ id: t, title: t })) || []}
        onIndustriesChange={industries => {
          updateProductIPR({ TargetIndustry: industries.map(i => i.id) });
        }}
        warning={warnings.targetIndustry}
      />
    </>
  );
}

/**
 * Product type comes first: Recyclability and the business model attributes only count when it is
 * set, and the tab's guidance tells users to start there.
 */
export function AdvancedAttributesSection({ mergedIpr, updateProductIPR, warnings }: AttributesSectionProps) {
  const specialAttributes = getSpecialAttributesFromIPR(mergedIpr);
  const onSpecialAttributeChange = (attribute: keyof IPRAttributes, value: boolean) => {
    updateProductIPR({ [attribute]: value });
  };
  return (
    <>
      <ProductTypeAttributes
        attributes={getProductTypeAttributesFromIPR(mergedIpr)}
        isManufactured={mergedIpr.isManufactured}
        tangibility={mergedIpr.Tangibility}
        offeringType={mergedIpr.OfferingType}
        onIsManufacturedChange={value => {
          updateProductIPR({ isManufactured: value });
        }}
        onTangibilityChange={value => {
          updateProductIPR({ Tangibility: value });
        }}
        onOfferingTypeChange={value => {
          updateProductIPR({ OfferingType: value });
        }}
      />
      <SpecialAttributes
        section="businessModel"
        attributes={specialAttributes}
        onAttributeChange={onSpecialAttributeChange}
        warning={warnings.businessModel}
      />
      <SpecialEndUses
        attributes={getSpecialEndUsesFromIPR(mergedIpr)}
        onAttributesChange={(attribute, value) => {
          updateProductIPR({ [attribute]: value });
        }}
      />
      <AbsentRawMaterials
        absentMaterials={mergedIpr.MaterialsAbsences?.map(m => ({ id: m, title: m })) || []}
        onAbsentMaterialsChange={absentMaterials => {
          updateProductIPR({ MaterialsAbsences: absentMaterials.map(m => m.id) });
        }}
      />
      <SpecialAttributes
        section="specialAttributes"
        attributes={specialAttributes}
        onAttributeChange={onSpecialAttributeChange}
      />
    </>
  );
}
